/** Route: `/betting-gaming/responsible-gambling` */

const IMG =
  "https://images.unsplash.com/photo-1554200876-56c2f25224ba?auto=format&fit=crop&w=1500&q=80";

export const RESPONSIBLE_GAMBLING_SLUG = "responsible-gambling";

export const responsibleGamblingDefaultData = {
  heroImage: IMG,
  galleryImages: [
    "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1563986768494-d09304a9b784?auto=format&fit=crop&w=900&q=80",
  ],
  stats: [
    { value: "18+", label: "Legal age" },
    { value: "Act 721", label: "Gaming law" },
    { value: "GGC", label: "Regulator" },
  ],
  funnelTagline:
    "Ghana-focused gambling education — laws, taxes, age limits, scams, and habits that keep betting from harming your life.",
  primaryCtaHeading: "Understand Ghana’s rules and bet with your eyes open",

  introKickoff: {
    chipCategory: "Betting & Gaming · Ghana",
    chipSecondary: "Laws & healthy habits",
    headline: "RESPONSIBLE GAMBLING HABITS",
    lead:
      "Betting and apps are everywhere — this hub explains what the law says, who regulates the industry, how taxes work, and how to protect your money, family, and mental health.",
    everydayLabels: [
      "Legal under regulated conditions",
      "Gaming Commission oversight",
      "Age limits & ID checks",
      "Taxes may apply to winnings",
    ],
    ghanaContext: [
      "Smartphones & Mobile Money",
      "Football culture & easy app access",
      "Social media hype & “easy money” stories",
      "Growing need for clear education",
    ],
    researchNote:
      "Gambling education and responsible gaming awareness are becoming increasingly important as participation grows.",
    focusTags: ["Act 721", "GGC licensing", "18+ only", "Scam awareness", "MoMo discipline"],
  },

  whyImportant: {
    headline: "WHY THIS CATEGORY IS IMPORTANT IN GHANA",
    growthTitle: "Betting and online gaming are growing rapidly because of:",
    growth: ["Smartphones", "Mobile Money", "Football popularity", "Easy access to betting apps"],
    gapsTitle: "At the same time, many people:",
    gaps: [
      "Do not fully understand gambling laws",
      "Are unaware of age restrictions",
      "Don’t understand taxes",
      "Struggle with gambling addiction",
      "Use unlicensed betting platforms",
    ],
    closing:
      "This is why gambling education and responsible gaming awareness are becoming increasingly important.",
  },

  plainLanguage: {
    headline: "WHAT IS RESPONSIBLE GAMBLING? (PLAIN LANGUAGE)",
    lead: "Responsible gambling means betting in a safe, controlled, and balanced way without harming:",
    safeFor: ["Your finances", "Your family", "Your mental health", "Your responsibilities"],
    meansTitle: "It means:",
    means: ["Gambling for entertainment", "Setting limits", "Avoiding addiction", "Understanding risks"],
  },

  legalYes: {
    headline: "IS BETTING LEGAL IN GHANA?",
    answer: "Yes — betting and gambling are legal in Ghana under regulated conditions.",
    intro: "The industry is governed mainly by:",
    actName: "Gaming Act, 2006 (Act 721)",
    actRegulatesTitle: "This law regulates:",
    regulates: ["Sports betting", "Casinos", "Online gaming", "Lotteries", "Gaming operators"],
    sourceNote: "Official regulator: gamingcommission.gov.gh",
  },

  regulator: {
    headline: "WHO REGULATES GAMBLING IN GHANA?",
    bodyLead: "The gambling industry is supervised by:",
    commissionName: "Gaming Commission of Ghana",
    responsibilitiesTitle: "The Gaming Commission is responsible for:",
    responsibilities: [
      "Licensing operators",
      "Monitoring betting companies",
      "Protecting consumers",
      "Promoting responsible gaming",
      "Preventing illegal gambling",
    ],
    regulatesTitle: "The Commission regulates:",
    regulates: ["Sports betting companies", "Casinos", "Online gaming platforms", "Route operations"],
    sourceNote: "gamingcommission.gov.gh",
  },

  legalAge: {
    headline: "WHAT IS THE LEGAL BETTING AGE IN GHANA?",
    banner: "18 years and above",
    lead: "Under Ghanaian gaming regulations:",
    rules: ["Persons under 18 years are not allowed to gamble legally."],
    verificationTitle: "Licensed betting operators may require:",
    verification: ["Ghana Card", "ID verification", "KYC checks"],
    verificationNote: "to confirm age and identity.",
  },

  whyAgeMatters: {
    headline: "WHY AGE RESTRICTIONS MATTER",
    vulnerableTitle: "Young people are more vulnerable to:",
    vulnerable: ["Gambling addiction", "Emotional decision-making", "Financial harm"],
    thereforeTitle: "This is why:",
    therefore: ["Underage gambling is prohibited", "Betting operators perform age verification"],
  },

  taxed: {
    headline: "IS BETTING TAXED IN GHANA?",
    answer: "Yes — Ghana has introduced taxes affecting betting and gaming winnings.",
    lead: "Tax laws and rates may change over time through:",
    changeVectors: ["National budget changes", "Tax amendments", "Government policy reviews"],
    sourceHint: "Consult current Ghana Revenue Authority materials and reputable tax summaries (e.g. professional advisories) for up-to-date rates.",
  },

  responsibleCore: {
    headline: "WHAT IS RESPONSIBLE GAMING REALLY ABOUT?",
    pillars: ["Control", "Awareness", "Safety", "Financial discipline"],
    meansTitle: "Responsible gambling means:",
    practices: [
      {
        key: "afford",
        label: "Betting only what you can afford to lose",
        intro: "Never gamble with:",
        never: ["Rent money", "School fees", "Loan money"],
      },
      {
        key: "time",
        label: "Setting time limits",
        body: "Avoid excessive betting sessions.",
      },
      {
        key: "spend",
        label: "Setting spending limits",
        body: "Control betting budgets carefully.",
      },
      {
        key: "risk",
        label: "Understanding gambling risks",
        body: "No betting system guarantees profits.",
      },
    ],
  },

  commonQna: [
    {
      id: "guaranteed-money",
      question: "Is betting a guaranteed way to make money?",
      answerBanner: "No",
      intro: "Betting is:",
      traits: ["Probability-based", "Risk-based", "Unpredictable"],
      closing: "Even expert bettors lose bets regularly.",
    },
    {
      id: "sure-odds",
      question: "Are sure odds real?",
      intro: "Most “sure odds” or “fixed matches” promoted online are scams.",
      scamsTitle: "Common scams",
      scams: ["Telegram betting scams", "Fake fixed matches", "Fake prediction groups", "Fake betting apps"],
      important: "No one can guarantee football results.",
    },
    {
      id: "how-responsible",
      question: "How do I gamble responsibly?",
      tipsTitle: "Practical responsible gambling tips",
      tips: [
        { title: "Set betting limits", body: "Decide how much you can afford to lose." },
        { title: "Take breaks", body: "Avoid continuous betting." },
        { title: "Don’t chase losses", body: "Trying to recover losses emotionally leads to bigger problems." },
      ],
      emotionalTitle: "Avoid emotional betting",
      emotionalIntro: "Never bet because:",
      emotionalNever: ["You are angry", "Desperate", "Depressed", "Under pressure"],
      prioritize: "Prioritize real-life responsibilities — family, work, and education come first.",
    },
  ],

  warningSigns: {
    headline: "WARNING SIGNS OF GAMBLING ADDICTION",
    signsTitle: "Common warning signs",
    signs: [
      "Betting daily uncontrollably",
      "Borrowing money to gamble",
      "Hiding gambling activity",
      "Depression after losses",
      "Gambling affecting relationships",
      "Skipping work or school because of betting",
    ],
    important: "Gambling addiction is a real mental health and financial problem.",
  },

  struggleFactors: {
    headline: "WHY MANY GHANAIANS STRUGGLE WITH GAMBLING",
    factorsTitle: "Several factors contribute:",
    factors: [
      "Unemployment pressure",
      "Desire for quick money",
      "Social media hype",
      "Peer pressure",
      "Mobile Money convenience",
    ],
    socialTitle: "Social media has increased risk",
    socialLead: "Many influencers promote:",
    socialPromotes: ["Unrealistic winnings", "Fake betting lifestyles", "“Easy money” narratives"],
    socialClosing: "This creates dangerous expectations.",
  },

  safePlatforms: {
    headline: "HOW TO CHOOSE SAFE BETTING PLATFORMS",
    licensedTitle: "Use licensed operators only",
    licensedIntro: "Good platforms usually provide:",
    licensed: [
      "Gaming Commission licensing",
      "Customer support",
      "Responsible gaming tools",
      "Secure payment systems",
    ],
    avoidTitle: "Avoid:",
    avoid: [
      "Fake betting apps",
      "Unlicensed operators",
      "Platforms requesting suspicious payments",
      "Betting agents demanding passwords or MoMo PINs",
    ],
  },

  momoRisks: {
    headline: "MOBILE MONEY & GAMBLING RISKS",
    lead: "Mobile Money has made betting:",
    pros: ["Faster", "Easier", "More accessible"],
    butTitle: "But also:",
    con: "Easier to overspend impulsively.",
    tipsTitle: "Safety tips",
    tips: ["Set MoMo spending limits", "Track betting expenses", "Avoid repeated deposits after losses"],
  },

  selfExclusion: {
    headline: "WHAT IS SELF-EXCLUSION?",
    lead: "Some betting platforms allow users to:",
    options: ["Temporarily block themselves", "Suspend accounts", "Set betting limits"],
    closing: "This helps users regain control.",
  },

  responsibleTools: {
    headline: "RESPONSIBLE GAMBLING TOOLS",
    lead: "Many betting platforms now offer:",
    tools: ["Deposit limits", "Time reminders", "Loss limits", "Account suspension tools"],
    closing: "These tools help reduce gambling harm.",
  },

  familiesWatch: {
    headline: "WHAT FAMILIES SHOULD LOOK OUT FOR",
    lead: "Families should watch for:",
    signs: ["Secretive behavior", "Sudden debt", "Excessive phone betting", "Emotional instability"],
  },

  safetyAdvice: {
    headline: "IMPORTANT SAFETY ADVICE FOR GHANAIANS",
    rememberTitle: "Remember:",
    remember: "Betting should be entertainment — not financial survival.",
    neverTitle: "Never:",
    never: ["Borrow to gamble", "Gamble under pressure", "Depend on betting income", "Ignore addiction signs"],
  },

  contentIdeas: {
    headline: "HIGH-VALUE CONTENT IDEAS (FOR YOUR SITE)",
    groups: [
      {
        title: "Gambling laws explained simply",
        bullets: ["Legal betting", "Gaming Commission", "Taxes", "Age limits"],
      },
      { title: "Responsible gambling guides", bullets: ["Safe betting habits", "Financial control", "Self-discipline"] },
      { title: "Addiction awareness content", bullets: ["Identify warning signs early"] },
      { title: "Scam awareness guides", bullets: ["Fake tipsters", "Fake apps", "“Fixed match” scams"] },
      { title: "Safe betting practices", bullets: ["Platform verification", "Secure MoMo use", "Account protection"] },
    ],
  },

  funnel: {
    headline: "INFOBOXX FUNNEL (HOW THIS PAGE CONVERTS)",
    hook: "“Is betting legal and safe in Ghana?”",
    education: "Teach laws, taxes, risks, responsible gambling",
    trust: "Localized Ghana-focused legal and safety guidance",
    conversion: [
      "Subscribe to responsible gaming content",
      "Join betting education community",
      "Receive SMS awareness tips",
      "Access safe betting guides",
    ],
  },

  strongCta: {
    headline: "STRONG CTA SECTION",
    items: [
      "Learn Ghana’s gambling laws in simple language",
      "Protect yourself from betting scams",
      "Get responsible gambling tips via SMS",
      "Learn how to bet safely and responsibly",
    ],
  },

  takeaway: {
    headline: "FINAL TAKEAWAY",
    paragraphs: [
      "Gambling and betting are legal in Ghana under Gaming Commission regulation.",
      "Users must understand that betting always involves risk, gambling should never replace income, addiction is real, and responsible gaming is essential.",
    ],
    footnote:
      "The safest approach is to gamble responsibly, use licensed platforms, protect your finances, and treat betting as entertainment — not guaranteed profit. Ghana’s betting and gaming industry continues to grow rapidly under the supervision of the Gaming Commission of Ghana (gamingcommission.gov.gh).",
  },

  editorialPlaybook: {
    leadCtasHeading: "Editorial lanes",
    leadCtas: [
      "Plain-language summaries of Act 721 and consumer duties",
      "Scam pattern libraries (Telegram, “fixed games”, fake apps)",
      "Family-facing checklists for early intervention",
    ],
    videos: ["What licensed books look like", "MoMo discipline for bettors"],
  },

  faqs: [
    {
      q: "Where do I confirm if an operator is licensed?",
      a: "Check the Gaming Commission of Ghana’s public registers and the operator’s own licensing disclosures — avoid “agents” who cannot show verifiable licensing.",
    },
    {
      q: "Does paying tax on winnings mean betting is “safe money”?",
      a: "No. Taxes are a legal compliance topic — they do not remove gambling risk or guarantee profit.",
    },
    {
      q: "What if I think I have a gambling problem?",
      a: "Seek professional help, tell someone you trust, use operator self-exclusion where available, and avoid chasing losses.",
    },
  ],

  articles: [
    {
      id: "rg-a-1",
      slug: "ghana-gaming-act-721-plain-language",
      title: "Ghana’s Gaming Act (721) in plain language",
      excerpt: "What is regulated, who licenses operators, and what “legal betting” actually means for users.",
      type: "Guide",
      readTime: "9 min read",
      author: "InfoBoxx Editorial",
      publishedAt: "2026",
      updatedAt: "2026",
      socialText: "Betting law basics for Ghana",
      coverImage: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80",
      tags: ["Law", "Ghana", "GGC"],
      pullQuote: "Legal does not mean risk-free.",
      takeaways: ["Act 721 scope", "Licensed vs unlicensed", "Age rules"],
      body: [{ heading: "Start here", text: "Separate legality from financial safety." }],
    },
    {
      id: "rg-a-2",
      slug: "telegram-betting-scams-ghana",
      title: "Telegram “sure odds” and fixed-match scams",
      excerpt: "How hype channels use urgency and fake proof to get MoMo access.",
      type: "Safety guide",
      readTime: "7 min read",
      author: "Consumer Guidance Unit",
      publishedAt: "2026",
      updatedAt: "2026",
      socialText: "Scam awareness",
      coverImage: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=800&q=80",
      tags: ["Scams", "MoMo"],
      pullQuote: "Nobody can sell certainty in random outcomes.",
      takeaways: ["Red flags", "PIN safety", "Verify licensing"],
      body: [{ heading: "Pattern", text: "Pressure + secrecy + payment tricks." }],
    },
    {
      id: "rg-a-3",
      slug: "momo-betting-limits-self-control",
      title: "Mobile Money made betting easy — set rails",
      excerpt: "Spending caps, session breaks, and tracking deposits without shame.",
      type: "Practical guide",
      readTime: "6 min read",
      author: "InfoBoxx Editorial",
      publishedAt: "2026",
      updatedAt: "2026",
      socialText: "MoMo discipline",
      coverImage: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&w=800&q=80",
      tags: ["MoMo", "Habits"],
      pullQuote: "Speed is a feature — impulse is the risk.",
      takeaways: ["Limits", "Cooldowns", "Receipts"],
      body: [{ heading: "Habit", text: "Treat deposits like a budget line, not a reflex." }],
    },
  ],

  videos: [
    {
      id: "rg-v-1",
      title: "Licensed vs unlicensed betting (Ghana)",
      duration: "2:20",
      poster: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=800&q=80",
      description: "What to verify before you deposit.",
    },
    {
      id: "rg-v-2",
      title: "Responsible gambling habits in 90 seconds",
      duration: "1:30",
      poster: "https://images.unsplash.com/photo-1563986768494-d09304a9b784?auto=format&fit=crop&w=800&q=80",
      description: "Limits, breaks, and when to stop.",
    },
  ],

  podcasts: [
    {
      id: "rg-p-1",
      episodeLabel: "Episode 1",
      duration: "24 min",
      title: "Gaming Commission role & family conversations on betting",
      description: "Regulation, youth risk, and how households can set boundaries.",
      host: "PlaySmart Ghana · Infoboxx",
      publishedAt: "2026",
      coverImage: "https://images.unsplash.com/photo-1554200876-56c2f25224ba?auto=format&fit=crop&w=800&q=80",
    },
  ],
};
